import React from 'react';
import PropTypes from 'prop-types';
import Tag from '../Tag/Tag';
import Input from '../Input';
import { st, classes } from './InputWithTags.st.css';
import classNames from 'classnames';
import isUndefined from 'lodash/isUndefined';
import SortableListBase from '../SortableListBase/SortableListBase';
import defaultDndStyles from '../dnd-styles';
import StatusIndicator from '../StatusIndicator';
import {
  StatusContext,
  getStatusFromContext,
  getAriaAttributesFromContext,
} from '../FormField/StatusContext';
import { DropDownArrow } from '@wix/wix-ui-icons-common/system';
import { WixStyleReactContext } from '../WixStyleReactProvider/context';

class InputWithTags extends React.Component {
  static contextType = StatusContext;

  constructor(props) {
    super(props);
    this.focus = this.focus.bind(this);
    this.blur = this.blur.bind(this);
    this.select = this.select.bind(this);
    this.renderReorderableTag = this.renderReorderableTag.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);

    this.state = { inputValue: '', inputHasFocus: false };
  }

  componentDidMount() {
    this.props.autoFocus && this.props.onFocus();
  }

  handleClick(e) {
    if (!this.props.disabled) {
      this.input.focus();
      this.props.onInputClicked && this.props.onInputClicked(e);
    }
  }

  handleInputFocus(e) {
    this.setState({ inputHasFocus: true });
    this.props.onFocus && this.props.onFocus(e);
  }

  handleInputBlur(e) {
    this.setState({ inputHasFocus: false });
    this.props.onBlur && this.props.onBlur(e);
  }

  _isSelectMode() {
    return this.props.mode === 'select';
  }

  _hasFocus() {
    return this.state.inputHasFocus && !this.props.disabled;
  }

  _getRootClassName(newColorsBranding) {
    const {
      status,
      disabled,
      readOnly,
      size = 'medium',
      border,
      className,
      maxHeight,
      maxNumRows,
    } = this.props;

    const isHasMaxHeight = !isUndefined(maxHeight) || !isUndefined(maxNumRows);

    const appliedClasses = [
      className,
      this._isSelectMode() && classes.selectMode,
      isHasMaxHeight && classes.hasMaxHeight,
    ].filter(Boolean);

    return st(
      classes.root,
      {
        border,
        disabled,
        hasFocus: this._hasFocus(),
        status: getStatusFromContext(this.context, status),
        readOnly,
        newColorsBranding,
        size,
      },
      ...appliedClasses,
    );
  }

  render() {
    const {
      tags,
      onRemoveTag,
      onReorder,
      placeholder,
      status,
      statusMessage,
      disabled,
      readOnly,
      delimiters,
      mode,
      size = 'medium',
      border,
      className,
      ...inputProps
    } = this.props;

    /* eslint-disable no-unused-vars */
    const {
      onManuallyInput,
      inputElement,
      closeOnSelect,
      predicate,
      onClickOutside,
      fixedHeader,
      fixedFooter,
      dataHook,
      onFocus,
      withSelection,
      onBlur,
      menuArrow,
      onInputClicked,
      ...desiredProps
    } = inputProps;

    let rowMultiplier;
    if (tags.length && tags[0].size === 'large') {
      rowMultiplier = 47;
    } else {
      rowMultiplier = 35;
    }
    const maxHeight =
      this.props.maxHeight ||
      this.props.maxNumRows * rowMultiplier ||
      'initial';

    const finalStatus = getStatusFromContext(this.context, status);

    return (
      <WixStyleReactContext.Consumer>
        {({ newColorsBranding }) => (
          <div
            className={this._getRootClassName(newColorsBranding)}
            style={{ maxHeight }}
            onClick={this.handleClick}
            data-hook={this.props.dataHook}
            data-focus={this._hasFocus() || null}
            data-status={finalStatus}
            {...getAriaAttributesFromContext(this.context)}
          >
            {onReorder ? (
              <SortableListBase
                contentClassName={classes.tagsContainer}
                items={tags}
                onDrop={onReorder}
                renderItem={this.renderReorderableTag}
              />
            ) : (
              tags.map(({ label, ...rest }) => (
                <Tag
                  key={rest.id}
                  dataHook="tag"
                  disabled={disabled}
                  onRemove={onRemoveTag}
                  {...rest}
                >
                  {label}
                </Tag>
              ))
            )}

            <span
              className={classNames(classes.inputWrapper, !tags.length)}
              data-hook="inner-input-with-tags"
            >
              <div className={classNames(classes.hiddenDiv)}>
                {this.state.inputValue}
              </div>

              <Input
                className={classes.input}
                size={size}
                width={this.props.width}
                ref={input => (this.input = input)}
                onFocus={this.handleInputFocus}
                onBlur={this.handleInputBlur}
                placeholder={tags.length === 0 ? placeholder : ''}
                {...desiredProps}
                dataHook="inputWithTags-input"
                disabled={disabled}
                readOnly={readOnly}
                disableEditing={this._isSelectMode()}
                onChange={e => {
                  if (!delimiters.includes(e.target.value)) {
                    this.setState({ inputValue: e.target.value });
                    desiredProps.onChange && desiredProps.onChange(e);
                  }
                }}
                withSelection
                prefix={
                  this.props.customSuffix &&
                  !this.props.hideCustomSuffix &&
                  !this.state.inputHasFocus && (
                    <span
                      data-hook="custom-suffix"
                      className={classes.customSuffix}
                    >
                      {this.props.customSuffix}
                    </span>
                  )
                }
                hideStatusSuffix
              />
            </span>

            {/* Suffixes */}
            <div className={classes.inputSuffix}>
              {/* Status Indicator */}
              {!disabled &&
                (['error', 'warning', 'loading'].includes(status) ||
                  finalStatus === 'loading') && (
                  <div className={classes.statusIndicator}>
                    <StatusIndicator
                      status={finalStatus}
                      message={statusMessage}
                      dataHook="input-status"
                    />
                  </div>
                )}

              {/* Arrow */}
              {this._isSelectMode() && (
                <div className={classes.menuArrow} data-hook="input-menu-arrow">
                  <DropDownArrow />
                </div>
              )}
            </div>
          </div>
        )}
      </WixStyleReactContext.Consumer>
    );
  }

  renderReorderableTag({
    item: { id, label, ...itemProps },
    previewStyles,
    isPlaceholder,
    isPreview,
    ...rest
  }) {
    const { onRemoveTag, disabled } = this.props;
    const _classes = classNames({
      [defaultDndStyles.itemPlaceholder]: isPlaceholder,
      [classes.draggedTagPlaceholder]: isPlaceholder,
      [defaultDndStyles.itemPreview]: isPreview,
      [classes.draggedTag]: isPreview,
    });

    return (
      <div style={previewStyles}>
        <Tag
          id={id}
          dataHook="tag"
          disabled={disabled}
          className={_classes}
          onRemove={onRemoveTag}
          {...itemProps}
          {...rest}
        >
          {label}
        </Tag>
      </div>
    );
  }

  focus() {
    this.input.focus();
  }

  blur() {
    this.input.blur();
  }

  select() {
    this.input.select();
  }

  clear() {
    this.input.clear();
  }
}

InputWithTags.propTypes = {
  onRemoveTag: PropTypes.func,
  tags: PropTypes.array,
  onReorder: PropTypes.func,
  maxHeight: PropTypes.string,
  maxNumRows: PropTypes.number,
  onKeyDown: PropTypes.func,
  dataHook: PropTypes.string,
  placeholder: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onInputClicked: PropTypes.func,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,

  /** The status of the input */
  status: PropTypes.oneOf(['error', 'warning', 'loading']),

  /** Text to be shown in the status icon tooltip */
  statusMessage: PropTypes.string,
  mode: PropTypes.oneOf(['select']),
  delimiters: PropTypes.array,
  width: PropTypes.string,
  customSuffix: PropTypes.node,
  hideCustomSuffix: PropTypes.bool,
};

InputWithTags.defaultProps = {
  onRemoveTag: () => {},
  tags: [],
  placeholder: '',
  delimiters: [],
};

export default InputWithTags;
